/**
 * Azure AD OAuth2 authentication utilities for the Hackathon Manager frontend.
 */

import { apiRequest } from './api';
import { User } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';

export interface AzureADConfig {
  tenant_id: string;
  client_id: string;
  authority: string;
  redirect_uri: string;
  scopes: string[];
  endpoints: {
    authorization: string;
    token: string;
    userinfo: string;
  };
  environment: string;
}

export interface AuthResult {
  success: boolean;
  message: string;
  tokens?: {
    access_token: string;
    id_token: string;
    token_type: string;
    expires_in: number;
    scope: string;
  };
  error?: string;
}

/**
 * Get Azure AD configuration from the API
 */
export async function getAzureADConfig(): Promise<AzureADConfig> {
  const response = await apiRequest('/auth/azure-ad/config');
  
  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to get Azure AD config: ${error}`);
  }
  
  return await response.json();
}

/**
 * Check if Azure AD is available and configured
 */
export async function isAzureADAvailable(): Promise<boolean> {
  try {
    const response = await apiRequest('/auth/azure-ad/status');
    const status = await response.json();
    return status.available && status.endpoints_configured;
  } catch (error) {
    console.warn('Failed to check Azure AD status:', error);
    return false;
  }
}

/**
 * Generate the Azure AD authorization URL and redirect to it
 */
export async function initiateAzureADLogin(): Promise<void> {
  try {
    const config = await getAzureADConfig();
    
    // For local development, override redirect URI to localhost
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const redirectUri = isLocalhost 
      ? `${window.location.origin}/auth/callback`  // Use localhost:3005/auth/callback
      : config.redirect_uri;  // Use production redirect URI
    
    console.log(`Using redirect URI: ${redirectUri} (localhost: ${isLocalhost})`);
    
    // Generate state parameter for CSRF protection
    const state = generateRandomString(32);
    
    // Generate PKCE parameters
    const { codeVerifier, codeChallenge } = await generatePKCE();
    
    // Debug: Log the generated state and other parameters
    console.log('🔐 Generated authentication parameters:', {
      state: state,
      stateLength: state.length,
      redirectUri: redirectUri,
      codeVerifierLength: codeVerifier.length,
      codeChallengeLength: codeChallenge.length,
      timestamp: new Date().toISOString(),
      sessionStorageAvailable: typeof(Storage) !== 'undefined'
    });
    
    // Clear any previous authentication completion flag
    sessionStorage.removeItem('azure_ad_auth_completed');
    
    // Store state, code verifier, and effective redirect URI in session storage for validation later
    sessionStorage.setItem('azure_ad_state', state);
    sessionStorage.setItem('azure_ad_code_verifier', codeVerifier);
    sessionStorage.setItem('azure_ad_config', JSON.stringify({
      ...config,
      redirect_uri: redirectUri  // Store the effective redirect URI
    }));
    
    // Debug: Verify storage worked
    const verifyStoredState = sessionStorage.getItem('azure_ad_state');
    console.log('🔍 Session storage verification:', {
      stateStored: verifyStoredState === state,
      storedValue: verifyStoredState,
      originalValue: state,
      allAzureItems: Object.keys(sessionStorage).filter(key => key.startsWith('azure_ad_'))
    });
    
    // Build authorization URL with PKCE
    const authParams = new URLSearchParams({
      client_id: config.client_id,
      response_type: 'code',
      redirect_uri: redirectUri,
      scope: config.scopes.join(' '),
      state: state,
      response_mode: 'query',
      prompt: 'select_account', // Allow user to choose account
      code_challenge: codeChallenge,
      code_challenge_method: 'S256'
    });
    
    const authUrl = `${config.endpoints.authorization}?${authParams.toString()}`;
    
    console.log('Redirecting to Azure AD for authentication with PKCE...');
    
    // Redirect to Azure AD
    window.location.href = authUrl;
    
  } catch (error) {
    console.error('Failed to initiate Azure AD login:', error);
    throw new Error('Failed to start Azure AD authentication');
  }
}

/**
 * Handle Azure AD OAuth callback
 */
export async function handleAzureADCallback(searchParams: URLSearchParams): Promise<User> {
  console.log('🔄 Processing Azure AD callback...');
  
  // Check if authentication was already completed successfully
  const authCompleted = sessionStorage.getItem('azure_ad_auth_completed');
  if (authCompleted) {
    console.log('✅ Authentication already completed, skipping callback processing');
    throw new Error('Authentication already completed');
  }
  
  // Generate a unique processing ID to prevent double execution
  const processingId = `auth_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const currentProcessingId = sessionStorage.getItem('azure_ad_processing_id');
  
  console.log('🔍 Processing ID check:', {
    newProcessingId: processingId,
    currentProcessingId,
    isAlreadyProcessing: !!currentProcessingId,
    authCompleted: !!authCompleted
  });
  
  // Check if we're already processing (to prevent double execution in React StrictMode)
  if (currentProcessingId) {
    console.warn('⚠️ Authentication already being processed, preventing duplicate execution');
    throw new Error('Authentication already in progress');
  }
  
  // Set processing flag
  sessionStorage.setItem('azure_ad_processing_id', processingId);
  
  try {
    // Debug: Log all search parameters
    const allParams = Object.fromEntries(searchParams.entries());
    console.log('🔍 All callback URL parameters:', allParams);
    
    // Retrieve stored state, code verifier, and config
    const storedState = sessionStorage.getItem('azure_ad_state');
    const storedCodeVerifier = sessionStorage.getItem('azure_ad_code_verifier');
    const storedConfigStr = sessionStorage.getItem('azure_ad_config');
    const receivedState = searchParams.get('state');
    const authCode = searchParams.get('code');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    
    // Enhanced debugging for state validation
    console.log('🔍 Session storage contents before cleanup:', {
      storedStateExists: !!storedState,
      storedStateLength: storedState?.length,
      storedStateFull: storedState, // Log full state for debugging
      storedCodeVerifierExists: !!storedCodeVerifier,
      storedConfigExists: !!storedConfigStr,
      allSessionItems: Object.keys(sessionStorage).filter(key => key.startsWith('azure_ad_'))
    });
    
    console.log('🔍 Received callback parameters:', {
      receivedState: receivedState,
      receivedStateLength: receivedState?.length,
      hasAuthCode: !!authCode,
      authCodeLength: authCode?.length,
      error,
      errorDescription,
      currentUrl: window.location.href,
      timestamp: new Date().toISOString()
    });
    
    // Check for errors first (before cleaning up storage)
    if (error) {
      console.error('Azure AD authentication error:', error, errorDescription);
      // Clean up storage on error
      sessionStorage.removeItem('azure_ad_state');
      sessionStorage.removeItem('azure_ad_code_verifier');
      sessionStorage.removeItem('azure_ad_config');
      sessionStorage.removeItem('azure_ad_processing_id');
      sessionStorage.removeItem('azure_ad_auth_completed');
      throw new Error(`Azure AD authentication failed: ${errorDescription || error}`);
    }
    
    // Validate state parameter with enhanced debugging
    console.log('🔒 State parameter validation:', {
      storedStateExists: !!storedState,
      receivedStateExists: !!receivedState,
      statesMatch: storedState === receivedState,
      storedStateFull: storedState,
      receivedStateFull: receivedState,
      lengthComparison: {
        stored: storedState?.length || 0,
        received: receivedState?.length || 0
      }
    });
    
    if (!storedState || !receivedState || storedState !== receivedState) {
      console.error('❌ State validation failed - detailed analysis:', {
        storedState: storedState || 'null',
        receivedState: receivedState || 'null',
        storedStateType: typeof storedState,
        receivedStateType: typeof receivedState,
        sessionStorageKeys: Object.keys(sessionStorage),
        possibleCauses: [
          'Session storage was cleared between requests',
          'Multiple authentication attempts in different tabs',
          'Browser security restrictions',
          'State parameter was modified in transit',
          'Callback URL was accessed directly without going through auth flow'
        ]
      });
      // Clean up storage on validation failure
      sessionStorage.removeItem('azure_ad_state');
      sessionStorage.removeItem('azure_ad_code_verifier');
      sessionStorage.removeItem('azure_ad_config');
      sessionStorage.removeItem('azure_ad_processing_id');
      sessionStorage.removeItem('azure_ad_auth_completed');
      throw new Error('Invalid state parameter - possible CSRF attack');
    }
    
    console.log('✅ State validation successful');
    
    // Validate authorization code
    if (!authCode) {
      console.error('No authorization code received');
      // Clean up storage on error
      sessionStorage.removeItem('azure_ad_state');
      sessionStorage.removeItem('azure_ad_code_verifier');
      sessionStorage.removeItem('azure_ad_config');
      sessionStorage.removeItem('azure_ad_processing_id');
      sessionStorage.removeItem('azure_ad_auth_completed');
      throw new Error('No authorization code received from Azure AD');
    }
    
    // Validate code verifier
    if (!storedCodeVerifier) {
      console.error('No code verifier found in session storage');
      // Clean up storage on error
      sessionStorage.removeItem('azure_ad_state');
      sessionStorage.removeItem('azure_ad_code_verifier');
      sessionStorage.removeItem('azure_ad_config');
      sessionStorage.removeItem('azure_ad_processing_id');
      sessionStorage.removeItem('azure_ad_auth_completed');
      throw new Error('No code verifier found - PKCE validation failed');
    }
    
    if (!storedConfigStr) {
      console.error('No stored Azure AD configuration found');
      // Clean up storage on error
      sessionStorage.removeItem('azure_ad_state');
      sessionStorage.removeItem('azure_ad_code_verifier');
      sessionStorage.removeItem('azure_ad_config');
      sessionStorage.removeItem('azure_ad_processing_id');
      sessionStorage.removeItem('azure_ad_auth_completed');
      throw new Error('No stored Azure AD configuration found');
    }
    
    const config = JSON.parse(storedConfigStr);
    console.log('Using stored config with redirect URI:', config.redirect_uri);
    
    // For SPA applications, we need to exchange the token directly from the frontend
    // This avoids the AADSTS9002327 error which occurs with server-side token exchange
    console.log('Exchanging authorization code for tokens directly from frontend...');
    
    const tokenData = {
      grant_type: 'authorization_code',
      client_id: config.client_id,
      code: authCode,
      redirect_uri: config.redirect_uri,
      code_verifier: storedCodeVerifier,
      scope: 'openid profile email User.Read'
    };
    
    const tokenUrl = `${config.authority}/oauth2/v2.0/token`;
    
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(tokenData),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Direct token exchange failed:', errorData);
      throw new Error(`Token exchange failed: ${response.status} ${response.statusText}`);
    }
    
    const tokens = await response.json();
    console.log('Direct token exchange successful');
    
    // Get user information from Microsoft Graph
    const userResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!userResponse.ok) {
      console.error('Failed to get user info from Microsoft Graph:', userResponse.status);
      throw new Error('Failed to retrieve user information');
    }
    
    const userInfo = await userResponse.json();
    const userEmail = userInfo.mail || userInfo.userPrincipalName;
    
    console.log('Retrieved user info from Microsoft Graph:', {
      id: userInfo.id,
      email: userEmail,
      name: userInfo.displayName
    });
    
    // Send user info to our backend to determine roles
    const backendResponse = await fetch(`${API_BASE_URL}/auth/azure-ad/user-info`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_info: userInfo,
        access_token: tokens.access_token,
        id_token: tokens.id_token
      }),
    });
    
    if (!backendResponse.ok) {
      const errorData = await backendResponse.json().catch(() => ({}));
      console.error('Backend user info processing failed:', errorData);
      throw new Error(`User info processing failed: ${backendResponse.status} ${backendResponse.statusText}`);
    }
    
    const backendData = await backendResponse.json();
    console.log('Backend user info processing successful');
    
    // Mark authentication as completed to prevent re-processing
    sessionStorage.setItem('azure_ad_auth_completed', 'true');
    
    // Clean up session storage after successful authentication
    sessionStorage.removeItem('azure_ad_state');
    sessionStorage.removeItem('azure_ad_code_verifier');
    sessionStorage.removeItem('azure_ad_config');
    sessionStorage.removeItem('azure_ad_processing_id');
    console.log('🧹 Cleaned up session storage after successful authentication');
    
    // Return user data with Azure AD ID token for authentication
    return {
      ...backendData.user,
      id_token: tokens.id_token,  // Include the Azure AD ID token
      access_token: tokens.access_token  // Include access token for Microsoft Graph calls
    };
    
  } catch (error) {
    console.error('Error during Azure AD authentication:', error);
    // Clean up session storage on error including processing and completion flags
    sessionStorage.removeItem('azure_ad_state');
    sessionStorage.removeItem('azure_ad_code_verifier');
    sessionStorage.removeItem('azure_ad_config');
    sessionStorage.removeItem('azure_ad_processing_id');
    sessionStorage.removeItem('azure_ad_auth_completed');
    throw new Error(`Failed to complete Azure AD authentication: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Generate a random string for state parameter
 */
function generateRandomString(length: number): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  
  for (let i = 0; i < length; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  
  return result;
}

/**
 * Generate PKCE code verifier and challenge
 */
async function generatePKCE(): Promise<{ codeVerifier: string; codeChallenge: string }> {
  // Generate code verifier (base64url-encoded random string)
  const codeVerifier = generateRandomString(128);
  
  // Generate code challenge (base64url-encoded SHA256 hash of code verifier)
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  
  // Convert ArrayBuffer to base64url
  const hashArray = Array.from(new Uint8Array(digest));
  const hashBinary = hashArray.map(b => String.fromCharCode(b)).join('');
  const codeChallenge = btoa(hashBinary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  
  return { codeVerifier, codeChallenge };
}

/**
 * Clear all Azure AD authentication data from session storage
 * Use this when starting a fresh authentication or on logout
 */
export function clearAzureADSession(): void {
  sessionStorage.removeItem('azure_ad_state');
  sessionStorage.removeItem('azure_ad_code_verifier');
  sessionStorage.removeItem('azure_ad_config');
  sessionStorage.removeItem('azure_ad_processing_id');
  sessionStorage.removeItem('azure_ad_auth_completed');
  console.log('🧹 Cleared all Azure AD session data');
}

/**
 * Check if authentication was recently completed
 */
export function isAuthenticationCompleted(): boolean {
  return sessionStorage.getItem('azure_ad_auth_completed') === 'true';
}

/**
 * Debug utility to check session storage state
 */
export function debugSessionStorage(): void {
  console.log('🔍 Session Storage Debug:', {
    isAvailable: typeof(Storage) !== 'undefined',
    length: sessionStorage.length,
    azureItems: Object.keys(sessionStorage)
      .filter(key => key.startsWith('azure_ad_'))
      .map(key => ({
        key,
        value: sessionStorage.getItem(key),
        length: sessionStorage.getItem(key)?.length || 0
      })),
    allItems: Object.keys(sessionStorage),
    timestamp: new Date().toISOString()
  });
}

/**
 * Check if the current URL contains OAuth2 callback parameters
 */
export function isOAuth2Callback(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('code') || urlParams.has('error');
}
